import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { IServiceRequest } from 'src/services/api/ServiceRequestDashboard/apiContracts';
import { CsvDateFormat } from 'src/services/client-types';
import {
  useDownloadCSVData,
  UseDownloadCSVDataReturn,
} from 'src/pages/SharedPageCustomHooks/UseDownloadCSV/useDownloadCSVData';
import {
  useDownloadPDF,
  UseDownloadPDFReturn,
} from 'src/pages/SharedPageCustomHooks/UseDownloadPDF/useDownloadPDF';
import {
  useServiceRequestDashboardApi,
  UseServiceRequestDashboardApiReturn,
} from 'src/services/api/ServiceRequestDashboard/serviceRequestDashboard';
import {
  useScopedServiceRequestsFormState,
  UseScopedServiceRequestsFormStateReturn,
} from 'src/pages/ServiceRequests/CustomHooks/useScopedServiceRequestsFormState';

const ADMIN_TIER_ROLES: ReadonlySet<string> = new Set<string>([
  'Admin',
  'Super Admin',
  'Property Developer',
  'Strata Manager',
]);

export interface UseContactServiceRequestsOrchestratorArgs {
  contactId: string;
}

export interface UseContactServiceRequestsOrchestratorReturn {
  serviceRequests: IServiceRequest[];
  visibleServiceRequests: IServiceRequest[];
  isLoading: boolean;
  refetch: () => Promise<void>;

  searchQuery: string;
  setSearchQuery: (query: string) => void;

  showResolvedOnly: boolean;
  toggleShowResolved: () => void;

  selectedIds: string[];
  allVisibleSelected: boolean;
  idsForCsvExport: string[];
  toggleSelection: (id: string) => void;
  toggleSelectionBatch: (ids: string[]) => void;
  toggleSelectAllVisible: () => void;
  clearSelection: () => void;

  expandedGroupIds: string[];
  toggleGroupExpansion: (id: string) => void;

  isDownloadOverlayOpen: boolean;
  openDownloadOverlay: () => void;
  closeDownloadOverlay: () => void;

  onDownloadCSV: (dateFormat: CsvDateFormat) => Promise<boolean>;
  onDownloadPDF: () => Promise<boolean>;
  isCSVLoading: boolean;
  isPDFLoading: boolean;

  showColumnSelectionDialog: boolean;
  openColumnSelectionDialog: () => void;
  closeColumnSelectionDialog: () => void;

  reset: () => void;
}

export const useContactServiceRequestsOrchestrator = ({
  contactId,
}: UseContactServiceRequestsOrchestratorArgs): UseContactServiceRequestsOrchestratorReturn => {
  const user: any = useSelector(selectAuthenticatedUser);
  const loginUserId: string = user?.id ?? '';
  const loginUserRole: string = user?.roles?.[0]?.role?.name ?? '';

  // Legacy parity: when the logged-in user has admin-tier permissions, fetch
  // the contact's SRs (looking up someone else's). Otherwise, fetch the
  // logged-in user's own SRs — preserves the role-gate from the legacy view.
  // The by-contact endpoint resolves the target's role server-side and scopes
  // to the caller's own visibility, so only the target id needs deciding here.
  const isAdminTierViewer: boolean = ADMIN_TIER_ROLES.has(loginUserRole);
  const targetUserId: string = isAdminTierViewer ? contactId : loginUserId;

  const srDashboardApi: UseServiceRequestDashboardApiReturn =
    useServiceRequestDashboardApi();

  const serviceRequests: IServiceRequest[] = srDashboardApi.serviceRequests;
  const isLoading: boolean = srDashboardApi.listLoading;

  const formState: UseScopedServiceRequestsFormStateReturn =
    useScopedServiceRequestsFormState({ serviceRequests });

  const csvDownload: UseDownloadCSVDataReturn = useDownloadCSVData();
  const pdfDownload: UseDownloadPDFReturn = useDownloadPDF();

  const onDownloadCSV = async (dateFormat: CsvDateFormat): Promise<boolean> => {
    return await csvDownload.onDownloadCSV(formState.idsForCsvExport, dateFormat);
  };

  const onDownloadPDF = async (): Promise<boolean> => {
    return await pdfDownload.onDownloadPDF(
      loginUserId,
      formState.idsForCsvExport.join('&')
    );
  };

  const refetch = async (): Promise<void> => {
    formState.clearSelection();

    await srDashboardApi.fetchByContact(targetUserId);
  };

  useEffect(() => {
    if (targetUserId) {
      void refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetUserId]);

  const reset = (): void => {
    formState.reset();
  };

  return {
    serviceRequests,
    visibleServiceRequests: formState.visibleServiceRequests,
    isLoading,
    refetch,

    searchQuery: formState.searchQuery,
    setSearchQuery: formState.setSearchQuery,

    showResolvedOnly: formState.showResolvedOnly,
    toggleShowResolved: formState.toggleShowResolved,

    selectedIds: formState.selectedIds,
    allVisibleSelected: formState.allVisibleSelected,
    idsForCsvExport: formState.idsForCsvExport,
    toggleSelection: formState.toggleSelection,
    toggleSelectionBatch: formState.toggleSelectionBatch,
    toggleSelectAllVisible: formState.toggleSelectAllVisible,
    clearSelection: formState.clearSelection,

    expandedGroupIds: formState.expandedGroupIds,
    toggleGroupExpansion: formState.toggleGroupExpansion,

    isDownloadOverlayOpen: formState.isDownloadOverlayOpen,
    openDownloadOverlay: formState.openDownloadOverlay,
    closeDownloadOverlay: formState.closeDownloadOverlay,

    onDownloadCSV,
    onDownloadPDF,
    isCSVLoading: csvDownload.isLoading,
    isPDFLoading: pdfDownload.isLoading,

    showColumnSelectionDialog: formState.showColumnSelectionDialog,
    openColumnSelectionDialog: formState.openColumnSelectionDialog,
    closeColumnSelectionDialog: formState.closeColumnSelectionDialog,

    reset,
  };
};
